import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { statusFor } from "../lib/status";
import { RiskGauge } from "./RiskGauge";
import { submitFeedback, apiRequestExpert } from "../lib/api";
import { AlertTriangle, XCircle, Info, ChevronRight, ChevronDown, ThumbsUp, ThumbsDown, UserCheck, Check, Share2, ShieldAlert } from "lucide-react";

const signalStatusIcon = (level) => {
  const s = statusFor(level);
  const Icon = s.icon;
  return <Icon className={`h-4 w-4 ${s.text}`} />;
};

// Reusable result template for SAFE / CAUTION / DANGER / LIMITED.
export const ResultCard = ({ result, onCheckAnother, readOnly = false }) => {
  const { t } = useLang();
  const { user, reload } = useAuth();
  const navigate = useNavigate();
  const [fbSent, setFbSent] = useState(false);
  const [shared, setShared] = useState(false);
  const [fbComment, setFbComment] = useState("");
  const [openSignals, setOpenSignals] = useState({});
  const [expertBusy, setExpertBusy] = useState(false);
  const [expertState, setExpertState] = useState(null);
  const [expertMsg, setExpertMsg] = useState("");
  const isRealResult = result && !result.is_demo && result.analysis_id && result.analysis_id !== "demo" && !readOnly;

  const shareResult = async () => {
    const url = `${window.location.origin}/s/${result.analysis_id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "TrustWall", text: t("result.share.text"), url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true); setTimeout(() => setShared(false), 2500);
      }
    } catch (_) { /* user cancelled */ }
  };

  const sendFb = async (helpful) => {
    setFbSent(true);
    try { await submitFeedback({ analysisId: result.analysis_id, helpful, comment: fbComment }); } catch (_) { /* silent */ }
  };

  const useCredit = async () => {
    setExpertBusy(true); setExpertMsg("");
    try {
      await apiRequestExpert(result.analysis_id, "credit");
      await reload();
      setExpertState("in_review");
    } catch (err) {
      setExpertMsg(err?.response?.data?.detail || t("expert.error"));
    } finally { setExpertBusy(false); }
  };

  const payOnce = async () => {
    setExpertBusy(true); setExpertMsg("");
    try {
      const { checkout_url } = await apiRequestExpert(result.analysis_id, "checkout", window.location.origin);
      window.location.href = checkout_url;
    } catch (err) {
      setExpertMsg(err?.response?.data?.detail || t("expert.error"));
      setExpertBusy(false);
    }
  };

  if (!result) return null;

  const level = result.risk_level || "LIMITED";
  const s = statusFor(level);
  const Icon = s.icon;
  const statusLabel = t(`common.${level.toLowerCase()}`);

  return (
    <div
      data-testid="check-result-card"
      className={`tw-fade-up overflow-hidden rounded-3xl border ${s.border} ${s.bg} ${s.glow}`}
    >
      {/* Header */}
      <div className="flex flex-col gap-5 border-b border-slate-200 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div className="flex items-center gap-4">
          <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${s.bg} ring-1 ${s.ring}`}>
            <Icon className={`h-7 w-7 ${s.text}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span data-testid="check-status-badge" className={`text-2xl font-bold ${s.text}`}>{statusLabel}</span>
              {result.is_demo && (
                <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
                  {t("common.demo")}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-500">{t(`result.statusLine.${level}`)}</p>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <RiskGauge score={result.risk_score} color={s.gauge} label={t("result.riskScore")} />
        </div>
      </div>

      <div className="space-y-7 p-6 sm:p-8">
        {/* Summary */}
        {result.summary && (
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t("result.summary")}</h3>
            <p className="text-base leading-relaxed text-slate-700">{result.summary}</p>
          </section>
        )}

        {/* Limited reason */}
        {result.limited_reason && (
          <div className="flex gap-3 rounded-2xl border border-slate-500/25 bg-slate-500/10 p-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-600" />
            <p className="text-sm leading-relaxed text-slate-600">{result.limited_reason}</p>
          </div>
        )}

        {/* Reassuring note on a safe result */}
        {level === "SAFE" && !result.is_demo && (
          <div data-testid="result-safe-reassure" className="flex gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
            <p className="text-sm leading-relaxed text-emerald-800">{t("result.safeReassure")}</p>
          </div>
        )}

        {/* Signals */}
        {result.signals && result.signals.length > 0 && (
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t("result.signals")}</h3>
            <div className="grid items-start gap-3 sm:grid-cols-2">
              {result.signals.map((sig, i) => {
                const ss = statusFor(sig.level);
                const open = !!openSignals[i];
                const why = t(`result.signalInfo.${sig.key}`, t("result.signalInfo.default"));
                return (
                  <div key={i} data-testid={`result-signal-${i}`} className={`overflow-hidden rounded-2xl border ${ss.border} bg-slate-50`}>
                    <button
                      data-testid={`result-signal-toggle-${i}`}
                      onClick={() => setOpenSignals((s) => ({ ...s, [i]: !s[i] }))}
                      className="flex w-full items-center justify-between gap-2 p-4 text-left"
                    >
                      <span className="flex items-center gap-2">
                        {signalStatusIcon(sig.level)}
                        <span className="text-sm font-semibold text-slate-900">{sig.label}</span>
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="border-t border-slate-200 px-4 pb-4 pt-3" data-testid={`result-signal-detail-${i}`}>
                        {sig.detail && <p className="text-sm leading-relaxed text-slate-600">{sig.detail}</p>}
                        <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("result.signalWhyLabel")}</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-500">{why}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Advice */}
        {result.recommended_action && (
          <section className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-blue-700">
              <ChevronRight className="h-4 w-4" /> {t("result.advice")}
            </h3>
            <p className="text-sm leading-relaxed text-slate-700">{result.recommended_action}</p>
          </section>
        )}

        {/* Do not do */}
        {result.do_not_do && result.do_not_do.length > 0 && (
          <section className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.07] p-5">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-rose-300">
              <XCircle className="h-4 w-4" /> {t("result.doNotDo")}
            </h3>
            <ul className="space-y-2">
              {result.do_not_do.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-400" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Transparency disclaimer */}
        {result.transparency_disclaimer && (
          <div className="flex gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <p className="text-xs leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-500">{t("result.disclaimer")}: </span>
              {result.transparency_disclaimer}
            </p>
          </div>
        )}

        {/* Safeonweb — always forward suspicious messages to the official BE anti-phishing service */}
        <div data-testid="result-safeonweb" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900">{t("result.safeonweb.title")}</h3>
              <p className="mt-1 text-sm leading-relaxed text-amber-800">{t("result.safeonweb.text")}</p>
              <a
                data-testid="result-safeonweb-link"
                href={`mailto:${t("result.safeonweb.email")}`}
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100"
              >
                <ShieldAlert className="h-4 w-4" /> {t("result.safeonweb.cta")} ({t("result.safeonweb.email")})
              </a>
            </div>
          </div>
        </div>


        {/* Expert Review — human review of the AI result (privacy-safe snapshot only) */}
        {isRealResult && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5" data-testid="expert-review">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{t("expert.question")}</p>
                <p className="mt-1 text-sm text-slate-500">{t("expert.desc")}</p>
                <p className="mt-1 text-xs text-slate-500">{t("expert.sla")}</p>
              </div>
              <span className="shrink-0 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">€2,99</span>
            </div>
            <div className="mt-3 rounded-xl bg-slate-100 p-3" data-testid="expert-why">
              <p className="text-xs font-semibold text-slate-600">{t("expert.whyTitle")}</p>
              <ul className="mt-1.5 space-y-1">
                <li className="flex items-start gap-2 text-xs text-slate-500"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-700" />{t("expert.why1")}</li>
                <li className="flex items-start gap-2 text-xs text-slate-500"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-700" />{t("expert.why2")}</li>
              </ul>
            </div>
            {!user ? (
              <button data-testid="expert-login-btn" onClick={() => navigate("/login")}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8]">
                <UserCheck className="h-4 w-4" /> {t("expert.loginPrompt")}
              </button>
            ) : expertState === "in_review" ? (
              <p className="mt-3 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700" data-testid="expert-requested">{t("expert.inReview")}</p>
            ) : (
              <div className="mt-3 space-y-2">
                <button data-testid="expert-use-credit-btn" onClick={useCredit} disabled={expertBusy || (user.expert_credits || 0) < 1}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-50">
                  <UserCheck className="h-4 w-4" /> {t("expert.directCredit")}
                </button>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    {t("expert.creditsLeft")}: <span className="font-semibold text-slate-600">{user.expert_credits || 0}</span>
                    {" · "}
                    <button onClick={() => navigate("/account")} className="text-blue-700 hover:underline">{t("expert.buyCredits")}</button>
                  </p>
                  <button data-testid="expert-pay-btn" onClick={payOnce} disabled={expertBusy}
                    className="text-xs font-medium text-blue-700 hover:underline disabled:opacity-60">
                    {t("expert.payOnce")}
                  </button>
                </div>
                {expertMsg && <p className="text-xs text-amber-800" data-testid="expert-error">{expertMsg}</p>}
              </div>
            )}
          </div>
        )}

        {/* Feedback (real analyses only, privacy-aware) */}
        {isRealResult && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="result-feedback">
            {fbSent ? (
              <p className="text-sm font-medium text-emerald-700" data-testid="feedback-thanks">{t("result.feedback.thanks")}</p>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-600">{t("result.feedback.question")}</p>
                <textarea
                  data-testid="feedback-comment"
                  value={fbComment}
                  onChange={(e) => setFbComment(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder={t("result.feedback.commentPlaceholder")}
                  className="mt-3 w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-900 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none"
                />
                <div className="mt-3 flex gap-2">
                  <button data-testid="feedback-yes" onClick={() => sendFb(true)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-500/20">
                    <ThumbsUp className="h-4 w-4" /> {t("result.feedback.yes")}
                  </button>
                  <button data-testid="feedback-no" onClick={() => sendFb(false)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100">
                    <ThumbsDown className="h-4 w-4" /> {t("result.feedback.no")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {isRealResult && (
          <button onClick={shareResult} data-testid="result-share"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-blue-200 bg-blue-50 py-3 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100">
            <Share2 className="h-4 w-4" /> {shared ? t("result.share.copied") : t("result.share.button")}
          </button>
        )}

        {onCheckAnother && (
          <button
            onClick={onCheckAnother}
            data-testid="result-check-another"
            className="w-full rounded-full border border-slate-200 bg-slate-50 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
          >
            {t("result.checkAnother")}
          </button>
        )}
      </div>
    </div>
  );
};
